import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { isValidString, getAccessToken } from '../common/CommonUtilsMethods';
import { configureWorkyAmplify } from './WorkyAmplifyConfig';
import {
    confirmSignUpWithCode,
    isValidRegistrationPhoneNumber,
    resendSignUpConfirmationCode,
    signInWithEmailPassword,
    signUpWithEmailPassword,
} from './WorkyCognitoAuthService';
import { handleCognitoPasswordSignInOutcome } from './WorkyCustomAuthSignInOutcomeService';
import { WORKY_WEB_APP_PATHS } from '../WorkyWebAppPaths';
import { navigateToLogin } from './WorkyAuthNavigationService';
import {
    WORKY_SUPPORT_EMAIL,
    WORKY_SUPPORT_PHONE_DIGITS,
    WORKY_SUPPORT_PHONE_DISPLAY,
} from '../common/WorkySupportContactConstants';
import { WORKY_BRAND_LOGO_SRC } from '../common/WorkyBrandConstants';
import { resolveRegistrationInvite } from './WorkyRegistrationResolveInviteService';
import {
    RegistrationMatchType,
    RegistrationWizardStep,
} from './WorkyRegistrationInterfaces';
import {
    isValidWorkyPassword,
} from './WorkyPasswordValidationService';
import { WorkyPasswordCriteriaChecklist } from './WorkyPasswordCriteriaChecklist';
import { buildWorkyAuthPathWithEmail } from './WorkyAuthQueryParamService';
import { useWorkyVerificationResendCooldown } from './WorkyVerificationResendCooldownHook';
import { VerificationResendCooldownScope } from './WorkyVerificationResendCooldownInterfaces';
import { isUsernameAlreadyExistsSignUpFailure } from './WorkySignUpFailureService';
import { landingPageSeoByPath } from '../landing-page/seo/LandingPageSeoConfig';
import { useLandingPageSeo } from '../landing-page/seo/useLandingPageSeo';
import './WorkyLoginPage.css';

const EMAIL_QUERY_PARAM: string = 'email';
const PRIMARY_WORKY_CORP_ID_QUERY_PARAM: string = 'primaryWorkyCorpId';

const readQueryParamFromLocationSearch = (
    locationSearch: string,
    queryParamName: string
): string | null => {
    const queryParams: URLSearchParams = new URLSearchParams(locationSearch);
    const queryParamValue: string | null = queryParams.get(queryParamName);
    if (!isValidString(queryParamValue)) {
        return null;
    }
    return queryParamValue.trim();
};

const WorkySignUpPage = (): JSX.Element => {
    const history = useHistory();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();

    useLandingPageSeo(landingPageSeoByPath[WORKY_WEB_APP_PATHS.signup]);

    const initialEmailFromQuery: string | null = useMemo(
        (): string | null => readQueryParamFromLocationSearch(location.search, EMAIL_QUERY_PARAM),
        [location.search]
    );
    const initialPrimaryWorkyCorpIdFromQuery: string | null = useMemo(
        (): string | null =>
            readQueryParamFromLocationSearch(location.search, PRIMARY_WORKY_CORP_ID_QUERY_PARAM),
        [location.search]
    );

    const [currentStep, setCurrentStep] = useState<RegistrationWizardStep>(RegistrationWizardStep.EMAIL);
    const [emailAddress, setEmailAddress] = useState<string>(initialEmailFromQuery ?? '');
    const [primaryWorkyCorpId, setPrimaryWorkyCorpId] = useState<string | null>(
        initialPrimaryWorkyCorpIdFromQuery
    );
    const [agencyDisplayName, setAgencyDisplayName] = useState<string | null>(null);
    const [orgDisplayName, setOrgDisplayName] = useState<string | null>(null);
    const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [isPasswordCriteriaVisible, setIsPasswordCriteriaVisible] = useState<boolean>(false);
    const [verificationCode, setVerificationCode] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isResendingVerificationCode, setIsResendingVerificationCode] = useState<boolean>(false);
    const [registrationBlockedMatchType, setRegistrationBlockedMatchType] =
        useState<RegistrationMatchType | null>(null);

    const {
        isResendCooldownActive,
        resendCooldownSecondsRemaining,
        beginResendCooldown,
        formatResendButtonLabel,
    } = useWorkyVerificationResendCooldown({
        cooldownScope: VerificationResendCooldownScope.SIGNUP_VERIFY,
        emailAddress,
    });

    useEffect(() => {
        configureWorkyAmplify();

        const existingAccessToken: string | null = getAccessToken();
        if (isValidString(existingAccessToken)) {
            history.replace(WORKY_WEB_APP_PATHS.homepage);
        }
    }, [history]);

    const handleNavigateHomeClick = useCallback((): void => {
        history.push(WORKY_WEB_APP_PATHS.home);
    }, [history]);

    const handleSignInClick = useCallback((): void => {
        void navigateToLogin();
    }, []);

    const handleSignInWithEmailClick = useCallback((): void => {
        history.push(
            isValidString(emailAddress)
                ? buildWorkyAuthPathWithEmail(WORKY_WEB_APP_PATHS.login, emailAddress)
                : WORKY_WEB_APP_PATHS.login
        );
    }, [emailAddress, history]);

    const handleForgotPasswordFromRegistrationClick = useCallback((): void => {
        history.push(
            isValidString(emailAddress)
                ? buildWorkyAuthPathWithEmail(WORKY_WEB_APP_PATHS.forgotPassword, emailAddress)
                : WORKY_WEB_APP_PATHS.forgotPassword
        );
    }, [emailAddress, history]);

    const handleEmailStepContinue = useCallback(async (): Promise<void> => {
        if (isSubmitting) {
            return;
        }

        const normalizedEmailAddress: string = emailAddress.trim().toLowerCase();
        if (!isValidString(normalizedEmailAddress)) {
            enqueueSnackbar('Please enter your email address.', { variant: 'warning' });
            return;
        }

        setIsSubmitting(true);
        setBlockedMessage(null);
        setRegistrationBlockedMatchType(null);

        try {
            const lookupResponse = await resolveRegistrationInvite({
                jobberEmail: normalizedEmailAddress,
                primaryWorkyCorpId: primaryWorkyCorpId,
            });

            if (lookupResponse === null) {
                enqueueSnackbar('Unable to verify your invitation. Please try again.', { variant: 'error' });
                return;
            }

            if (!lookupResponse.canProceed) {
                setRegistrationBlockedMatchType(lookupResponse.matchType);
                setBlockedMessage(
                    lookupResponse.blockedMessage ??
                        'You have not been invited by a client on Worky, please reach out to your organization and request them to invite you'
                );
                setCurrentStep(RegistrationWizardStep.AGENCY_MATCH);
                return;
            }

            setEmailAddress(normalizedEmailAddress);
            setPrimaryWorkyCorpId(lookupResponse.primaryWorkyCorpId ?? primaryWorkyCorpId);
            setAgencyDisplayName(lookupResponse.agencyDisplayName ?? null);
            setOrgDisplayName(lookupResponse.orgDisplayName ?? null);
            setCurrentStep(RegistrationWizardStep.PROFILE);

            if (lookupResponse.matchType === RegistrationMatchType.SELF_SERVE_AGENCY) {
                enqueueSnackbar('Agency confirmed. Complete your profile to continue.', { variant: 'success' });
            } else {
                enqueueSnackbar('Invitation confirmed. Complete your profile to continue.', { variant: 'success' });
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [emailAddress, enqueueSnackbar, isSubmitting, primaryWorkyCorpId]);

    const handleProfileStepContinue = useCallback(async (): Promise<void> => {
        if (isSubmitting) {
            return;
        }

        if (!isValidString(firstName) || !isValidString(lastName)) {
            enqueueSnackbar('First and last name are required.', { variant: 'warning' });
            return;
        }

        if (!isValidString(phoneNumber)) {
            enqueueSnackbar('Phone number is required.', { variant: 'warning' });
            return;
        }

        if (!isValidRegistrationPhoneNumber(phoneNumber)) {
            enqueueSnackbar('Please enter a valid phone number.', { variant: 'warning' });
            return;
        }

        if (!isValidWorkyPassword(password)) {
            enqueueSnackbar('Please meet all password requirements before continuing.', {
                variant: 'warning',
            });
            return;
        }

        if (password !== confirmPassword) {
            enqueueSnackbar('Passwords do not match.', { variant: 'warning' });
            return;
        }

        setIsSubmitting(true);

        try {
            const signUpError = await signUpWithEmailPassword({
                emailAddress,
                password,
                firstName,
                lastName,
                phoneNumber,
            });

            if (signUpError !== null) {
                if (isUsernameAlreadyExistsSignUpFailure(signUpError)) {
                    setRegistrationBlockedMatchType(RegistrationMatchType.ALREADY_REGISTERED);
                    setBlockedMessage(signUpError.errorMessage);
                    setCurrentStep(RegistrationWizardStep.AGENCY_MATCH);
                    return;
                }

                enqueueSnackbar(signUpError.errorMessage, { variant: 'error' });
                return;
            }

            enqueueSnackbar('Account created. Check your email for a verification code.', { variant: 'success' });
            beginResendCooldown();
            setCurrentStep(RegistrationWizardStep.EMAIL_VERIFY);
        } finally {
            setIsSubmitting(false);
        }
    }, [
        confirmPassword,
        beginResendCooldown,
        emailAddress,
        enqueueSnackbar,
        firstName,
        isSubmitting,
        lastName,
        password,
        phoneNumber,
    ]);

    const handleVerifyAndCompleteSignIn = useCallback(async (): Promise<void> => {
        if (isSubmitting) {
            return;
        }

        if (!isValidString(verificationCode)) {
            enqueueSnackbar('Enter the verification code from your email.', { variant: 'warning' });
            return;
        }

        setIsSubmitting(true);

        try {
            const confirmError = await confirmSignUpWithCode({
                emailAddress,
                confirmationCode: verificationCode,
            });

            if (confirmError !== null) {
                enqueueSnackbar(confirmError.errorMessage, { variant: 'error' });
                return;
            }

            const signInOutcome = await signInWithEmailPassword({
                emailAddress,
                password,
            });

            await handleCognitoPasswordSignInOutcome({
                signInOutcome,
                history,
                enqueueSnackbar,
                primaryWorkyCorpId,
                successMessage: 'Welcome to Worky!',
            });
        } finally {
            setIsSubmitting(false);
        }
    }, [
        emailAddress,
        enqueueSnackbar,
        history,
        isSubmitting,
        password,
        primaryWorkyCorpId,
        verificationCode,
    ]);

    const handleResendVerificationCode = useCallback(async (): Promise<void> => {
        if (isResendingVerificationCode) {
            return;
        }

        if (isResendCooldownActive) {
            enqueueSnackbar(
                `Please wait ${resendCooldownSecondsRemaining} seconds before resending.`,
                { variant: 'warning' }
            );
            return;
        }

        setIsResendingVerificationCode(true);
        try {
            const resendError = await resendSignUpConfirmationCode(emailAddress);
            if (resendError !== null) {
                enqueueSnackbar(resendError.errorMessage, { variant: 'error' });
                return;
            }
            beginResendCooldown();
            enqueueSnackbar('Verification code resent.', { variant: 'success' });
        } finally {
            setIsResendingVerificationCode(false);
        }
    }, [
        beginResendCooldown,
        emailAddress,
        enqueueSnackbar,
        isResendCooldownActive,
        isResendingVerificationCode,
        resendCooldownSecondsRemaining,
    ]);

    const handlePasswordRecoveryClick = useCallback((): void => {
        history.push(
            buildWorkyAuthPathWithEmail(WORKY_WEB_APP_PATHS.forgotPassword, emailAddress)
        );
    }, [emailAddress, history]);

    const renderStepContent = (): JSX.Element => {
        if (currentStep === RegistrationWizardStep.EMAIL) {
            return (
                <>
                    <p className="worky-login-subtitle">Enter the email address you were invited with.</p>
                    <div className="worky-login-form">
                        <TextField
                            label="Email"
                            type="email"
                            autoComplete="email"
                            value={emailAddress}
                            onChange={(changeEvent): void => setEmailAddress(changeEvent.target.value)}
                            required
                            fullWidth
                        />
                        <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            className="worky-login-submit-button"
                            disabled={isSubmitting}
                            fullWidth
                            onClick={(): void => {
                                void handleEmailStepContinue();
                            }}
                        >
                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
                        </Button>
                    </div>
                </>
            );
        }

        if (currentStep === RegistrationWizardStep.AGENCY_MATCH && blockedMessage !== null) {
            const isAlreadyRegistered: boolean =
                registrationBlockedMatchType === RegistrationMatchType.ALREADY_REGISTERED;

            return (
                <>
                    <p className="worky-login-subtitle">{blockedMessage}</p>
                    {isAlreadyRegistered ? (
                        <>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                className="worky-login-submit-button"
                                fullWidth
                                onClick={handleSignInWithEmailClick}
                            >
                                Sign in
                            </Button>
                            <Button
                                type="button"
                                variant="outlined"
                                color="primary"
                                fullWidth
                                onClick={handleForgotPasswordFromRegistrationClick}
                            >
                                Forgot password
                            </Button>
                        </>
                    ) : null}
                    <Button
                        type="button"
                        variant="outlined"
                        fullWidth
                        onClick={(): void => {
                            setRegistrationBlockedMatchType(null);
                            setCurrentStep(RegistrationWizardStep.EMAIL);
                        }}
                    >
                        Try a different email
                    </Button>
                </>
            );
        }

        if (currentStep === RegistrationWizardStep.PROFILE) {
            const registrationJoinDisplayName: string | null = isValidString(orgDisplayName)
                ? orgDisplayName
                : agencyDisplayName;

            return (
                <>
                    {isValidString(registrationJoinDisplayName) ? (
                        <p className="worky-login-subtitle">
                            Joining <strong>{registrationJoinDisplayName}</strong>. Complete your profile.
                        </p>
                    ) : (
                        <p className="worky-login-subtitle">Complete your profile to create your account.</p>
                    )}
                    <div className="worky-login-form">
                        <TextField
                            label="First name"
                            value={firstName}
                            onChange={(changeEvent): void => setFirstName(changeEvent.target.value)}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Last name"
                            value={lastName}
                            onChange={(changeEvent): void => setLastName(changeEvent.target.value)}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Phone"
                            type="tel"
                            autoComplete="tel"
                            value={phoneNumber}
                            onChange={(changeEvent): void => setPhoneNumber(changeEvent.target.value)}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Password"
                            type="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={(changeEvent): void => setPassword(changeEvent.target.value)}
                            onFocus={(): void => setIsPasswordCriteriaVisible(true)}
                            onBlur={(): void => {
                                if (!isValidString(password)) {
                                    setIsPasswordCriteriaVisible(false);
                                }
                            }}
                            required
                            fullWidth
                        />
                        <WorkyPasswordCriteriaChecklist
                            password={password}
                            isVisible={isPasswordCriteriaVisible}
                        />
                        <TextField
                            label="Confirm password"
                            type="password"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(changeEvent): void => setConfirmPassword(changeEvent.target.value)}
                            required
                            fullWidth
                        />
                        <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            className="worky-login-submit-button"
                            disabled={isSubmitting}
                            fullWidth
                            onClick={(): void => {
                                void handleProfileStepContinue();
                            }}
                        >
                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Create account'}
                        </Button>
                    </div>
                </>
            );
        }

        return (
            <>
                <p className="worky-login-subtitle">
                    Enter the verification code sent to <strong>{emailAddress}</strong>.
                </p>
                <div className="worky-login-form">
                    <TextField
                        label="Verification code"
                        value={verificationCode}
                        onChange={(changeEvent): void => setVerificationCode(changeEvent.target.value)}
                        required
                        fullWidth
                    />
                    <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        className="worky-login-submit-button"
                        disabled={isSubmitting}
                        fullWidth
                        onClick={(): void => {
                            void handleVerifyAndCompleteSignIn();
                        }}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Verify and sign in'}
                    </Button>
                    <Button
                        type="button"
                        variant="text"
                        fullWidth
                        disabled={isSubmitting || isResendingVerificationCode || isResendCooldownActive}
                        onClick={(): void => {
                            void handleResendVerificationCode();
                        }}
                    >
                        {isResendingVerificationCode
                            ? 'Sending code…'
                            : formatResendButtonLabel('Resend code')}
                    </Button>
                    <Button type="button" variant="text" fullWidth onClick={handlePasswordRecoveryClick}>
                        Can&apos;t find your code? Reset your password
                    </Button>
                </div>
            </>
        );
    };

    return (
        <div className="worky-login-page">
            <button
                type="button"
                className="worky-login-back-home-button"
                onClick={handleNavigateHomeClick}
            >
                <ArrowBackIcon fontSize="small" />
                Back to home
            </button>
            <div className="worky-login-card">
                <button
                    type="button"
                    className="worky-login-logo-button"
                    onClick={handleNavigateHomeClick}
                    aria-label="Worky home"
                >
                    <img src={WORKY_BRAND_LOGO_SRC} alt="Worky" className="worky-login-logo-image" />
                </button>

                <h1 className="worky-login-title">Worky Sign Up</h1>
                {renderStepContent()}

                <div className="worky-login-footer">
                    Already have an account?{' '}
                    <span className="worky-login-footer-link" onClick={handleSignInClick} role="button" tabIndex={0}>
                        Sign in
                    </span>
                </div>

                <div className="worky-login-support-footer">
                    <span className="worky-login-support-label">Need help? Contact Us</span>{' '}
                    <a className="worky-login-support-link" href={`mailto:${WORKY_SUPPORT_EMAIL}`}>
                        {WORKY_SUPPORT_EMAIL}
                    </a>
                    {' · '}
                    <a className="worky-login-support-link" href={`tel:${WORKY_SUPPORT_PHONE_DIGITS}`}>
                        {WORKY_SUPPORT_PHONE_DISPLAY}
                    </a>
                </div>
            </div>
        </div>
    );
};

export { WorkySignUpPage };
